
import React, { useMemo, useState } from "react";

const FAQs = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [search, setSearch] = useState("");

  const faqData = [
    {
      category: "General",
      icon: "✦",
      items: [
        {
          q: "What is School Hub?",
          a: "School Hub is an online education platform offering high-quality courses in various fields like technology, business, design, and more. Our mission is to make quality education accessible to everyone, everywhere.",
        },
        {
          q: "How do I create an account?",
          a: "Click on the 'Sign Up' button on the top-right corner of our homepage. Fill in your name, email, and password, then verify your email address. Once verified, you can browse and enroll in any course.",
        },
        {
          q: "Is School Hub free to use?",
          a: "Creating an account and browsing courses is completely free. However, most courses require a one-time payment for enrollment. We also offer some free courses and trial content.",
        },
      ],
    },
    {
      category: "Courses & Learning",
      icon: "▣",
      items: [
        {
          q: "How long do I have access to a course?",
          a: "Once you enroll in a course, you get lifetime access to all its content — including future updates. You can learn at your own pace, anytime, anywhere.",
        },
        {
          q: "Do I get a certificate after completing a course?",
          a: "Yes! After successfully completing a course, including all lessons and quizzes, you'll receive a verified digital certificate that you can share on LinkedIn or add to your resume.",
        },
        {
          q: "Can I download course videos for offline viewing?",
          a: "Currently, offline downloads are not supported for most courses. However, our platform is mobile-friendly, so you can stream lessons on any device with an internet connection.",
        },
        {
          q: "What if I get stuck during a course?",
          a: "You can post questions in the course's discussion forum, where instructors and fellow learners can help you. You can also reach out to our support team anytime.",
        },
      ],
    },
    {
      category: "Payments & Refunds",
      icon: "₹",
      items: [
        {
          q: "What payment methods do you accept?",
          a: "We accept major credit/debit cards, UPI, net banking, and popular wallets. All payments are processed through secure, encrypted gateways.",
        },
        {
          q: "Can I get a refund?",
          a: "Yes, we offer a 7-day refund policy. If you're not satisfied with a course, contact our support team within 7 days of purchase. Refunds are processed within 7–10 business days.",
        },
        {
          q: "Are there any discounts or coupons?",
          a: "Yes! We regularly run seasonal sales and offer coupons. Subscribe to our newsletter and follow us on social media to stay updated on the latest offers.",
        },
      ],
    },
    {
      category: "Account & Technical",
      icon: "⚙",
      items: [
        {
          q: "I forgot my password. What should I do?",
          a: "Click on 'Forgot Password' on the login page, enter your registered email, and we'll send you a password reset link.",
        },
        {
          q: "Can I change my email address?",
          a: "Yes. Go to your Profile Settings → Account → Change Email. You'll need to verify the new email address before it's updated.",
        },
        {
          q: "The video isn't playing. What can I do?",
          a: "Try refreshing the page, clearing your browser cache, using a different browser such as Chrome or Firefox, and checking your internet connection.",
        },
        {
          q: "How do I delete my account?",
          a: "You can delete your account from Profile Settings → Account → Delete Account. Please note that this action is permanent and all your progress will be lost.",
        },
      ],
    },
  ];

  const allFaqs = useMemo(() => {
    let list = [];

    faqData.forEach((category) => {
      category.items.forEach((item) => {
        list.push({
          ...item,
          category: category.category,
          icon: category.icon,
        });
      });
    });

    return list;
  }, []);

  const filteredFaqs = allFaqs.filter((item) => {
    const text = `${item.q} ${item.a} ${item.category}`.toLowerCase();

    return text.includes(search.toLowerCase());
  });

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const styles = {
    page: {
      minHeight: "100vh",
      margin: 0,
      padding: 0,
      fontFamily:
        "'Inter', 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
      background:
        "linear-gradient(135deg,#f8faff 0%,#eef2ff 48%,#f5f3ff 100%)",
      color: "#0f172a",
    },

    header: {
      position: "sticky",
      top: 0,
      zIndex: 1000,
      background: "rgba(255,255,255,0.82)",
      backdropFilter: "blur(22px)",
      WebkitBackdropFilter: "blur(22px)",
      borderBottom: "1px solid rgba(148,163,184,0.16)",
      boxShadow: "0 8px 30px rgba(15,23,42,0.05)",
    },

    headerInner: {
      maxWidth: "1250px",
      margin: "auto",
      padding: "18px 25px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "20px",
      flexWrap: "wrap",
    },

    logo: {
      margin: 0,
      fontSize: "24px",
      fontWeight: 900,
      letterSpacing: "-0.5px",
      background: "linear-gradient(90deg,#312e81,#4f46e5,#7c3aed)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
    },

    nav: {
      display: "flex",
      gap: "7px",
      alignItems: "center",
      flexWrap: "wrap",
    },

    navLink: {
      textDecoration: "none",
      color: "#475569",
      fontSize: "14px",
      fontWeight: 600,
      padding: "10px 16px",
      borderRadius: "10px",
      transition: "0.25s",
    },

    hero: {
      position: "relative",
      overflow: "hidden",
      background:
        "radial-gradient(circle at 15% 20%,rgba(99,102,241,.30),transparent 25%), radial-gradient(circle at 85% 80%,rgba(168,85,247,.25),transparent 30%), linear-gradient(135deg,#111827,#1e1b4b 60%,#312e81)",
      padding: "80px 25px 95px",
      textAlign: "center",
      color: "#fff",
    },

    heroGlow1: {
      position: "absolute",
      width: "300px",
      height: "300px",
      borderRadius: "50%",
      background: "rgba(99,102,241,0.12)",
      filter: "blur(20px)",
      top: "-160px",
      left: "-80px",
    },

    heroGlow2: {
      position: "absolute",
      width: "260px",
      height: "260px",
      borderRadius: "50%",
      background: "rgba(168,85,247,0.14)",
      filter: "blur(20px)",
      bottom: "-140px",
      right: "-80px",
    },

    heroContent: {
      position: "relative",
      zIndex: 2,
      maxWidth: "850px",
      margin: "auto",
    },

    badge: {
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      padding: "8px 15px",
      borderRadius: "50px",
      background: "rgba(255,255,255,0.08)",
      border: "1px solid rgba(255,255,255,0.14)",
      color: "#c7d2fe",
      fontSize: "11px",
      fontWeight: 800,
      letterSpacing: "1px",
      marginBottom: "20px",
    },

    badgeDot: {
      width: "7px",
      height: "7px",
      borderRadius: "50%",
      background: "#818cf8",
      boxShadow: "0 0 0 5px rgba(129,140,248,.12)",
    },

    heroTitle: {
      margin: 0,
      fontSize: "clamp(38px,6vw,64px)",
      lineHeight: 1.04,
      letterSpacing: "-3px",
      fontWeight: 900,
    },

    heroGradient: {
      background:
        "linear-gradient(90deg,#a5b4fc,#c4b5fd,#93c5fd)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
    },

    heroText: {
      maxWidth: "650px",
      margin: "22px auto 0",
      color: "rgba(255,255,255,.68)",
      fontSize: "16px",
      lineHeight: 1.8,
    },

    searchWrapper: {
      position: "relative",
      maxWidth: "680px",
      margin: "-31px auto 0",
      zIndex: 10,
      padding: "0 20px",
    },

    searchBox: {
      height: "64px",
      display: "flex",
      alignItems: "center",
      gap: "12px",
      background: "rgba(255,255,255,.96)",
      border: "1px solid rgba(226,232,240,.9)",
      borderRadius: "20px",
      padding: "0 20px",
      boxSizing: "border-box",
      boxShadow:
        "0 20px 50px rgba(15,23,42,.14),0 5px 15px rgba(79,70,229,.08)",
    },

    searchIcon: {
      fontSize: "21px",
      color: "#6366f1",
    },

    searchInput: {
      flex: 1,
      height: "100%",
      border: 0,
      outline: 0,
      background: "transparent",
      fontSize: "14px",
      fontFamily: "inherit",
      color: "#0f172a",
    },

    clearButton: {
      border: 0,
      background: "#eef2ff",
      color: "#4f46e5",
      width: "30px",
      height: "30px",
      borderRadius: "9px",
      cursor: "pointer",
      fontWeight: 800,
    },

    main: {
      maxWidth: "1050px",
      margin: "0 auto",
      padding: "65px 25px 80px",
    },

    category: {
      marginBottom: "45px",
    },

    categoryHeader: {
      display: "flex",
      alignItems: "center",
      gap: "13px",
      marginBottom: "17px",
    },

    categoryIcon: {
      width: "44px",
      height: "44px",
      borderRadius: "13px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "linear-gradient(135deg,#eef2ff,#f3e8ff)",
      border: "1px solid #ddd6fe",
      color: "#6366f1",
      fontSize: "18px",
      fontWeight: 800,
    },

    categoryTitle: {
      margin: 0,
      fontSize: "19px",
      fontWeight: 850,
      color: "#111827",
    },

    categoryCount: {
      marginLeft: "auto",
      fontSize: "11px",
      fontWeight: 700,
      color: "#6366f1",
      background: "#eef2ff",
      padding: "6px 10px",
      borderRadius: "50px",
    },

    faqItem: {
      marginBottom: "12px",
      borderRadius: "17px",
      overflow: "hidden",
      background: "rgba(255,255,255,.92)",
      border: "1px solid #e2e8f0",
      boxShadow: "0 5px 18px rgba(15,23,42,.035)",
      transition: "all .3s ease",
    },

    faqQuestion: {
      minHeight: "68px",
      padding: "16px 20px",
      boxSizing: "border-box",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "15px",
      cursor: "pointer",
      color: "#1e293b",
      fontSize: "14px",
      fontWeight: 750,
    },

    faqQuestionOpen: {
      color: "#4f46e5",
      background: "linear-gradient(90deg,#f8f7ff,#eef2ff)",
    },

    questionText: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },

    questionNumber: {
      width: "30px",
      height: "30px",
      flexShrink: 0,
      borderRadius: "9px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#f1f5f9",
      color: "#64748b",
      fontSize: "11px",
      fontWeight: 800,
    },

    answer: {
      padding: "0 22px 21px 62px",
      color: "#64748b",
      fontSize: "14px",
      lineHeight: 1.8,
      background: "linear-gradient(90deg,#f8faff,#ffffff)",
      borderTop: "1px solid #e2e8f0",
    },

    plus: {
      width: "34px",
      height: "34px",
      flexShrink: 0,
      borderRadius: "10px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "20px",
      fontWeight: 500,
      background: "#f1f5f9",
      color: "#475569",
      transition: "all .3s",
    },

    plusOpen: {
      background: "linear-gradient(135deg,#4f46e5,#7c3aed)",
      color: "#fff",
      transform: "rotate(45deg)",
    },

    noResult: {
      textAlign: "center",
      padding: "60px 25px",
      background: "#fff",
      borderRadius: "22px",
      border: "1px solid #e2e8f0",
      boxShadow: "0 15px 40px rgba(15,23,42,.06)",
    },

    contact: {
      position: "relative",
      overflow: "hidden",
      marginTop: "30px",
      borderRadius: "25px",
      padding: "38px",
      textAlign: "center",
      color: "#fff",
      background:
        "radial-gradient(circle at top right,rgba(129,140,248,.35),transparent 30%), linear-gradient(135deg,#111827,#312e81)",
      boxShadow: "0 25px 60px rgba(49,46,129,.18)",
    },

    contactTitle: {
      margin: "0 0 8px",
      fontSize: "25px",
      fontWeight: 850,
    },

    contactText: {
      maxWidth: "550px",
      margin: "0 auto 22px",
      color: "rgba(255,255,255,.68)",
      fontSize: "14px",
      lineHeight: 1.7,
    },

    contactButton: {
      display: "inline-block",
      textDecoration: "none",
      background: "#fff",
      color: "#4338ca",
      padding: "12px 22px",
      borderRadius: "12px",
      fontSize: "13px",
      fontWeight: 800,
      transition: ".25s",
    },

    footer: {
      textAlign: "center",
      padding: "30px 20px",
      color: "#64748b",
      fontSize: "13px",
    },
  };

  return (
    <div style={styles.page}>
      {/* ================= HEADER ================= */}
  

      {/* ================= HERO ================= */}
      <section style={styles.hero}>
        <div style={styles.heroGlow1}></div>
        <div style={styles.heroGlow2}></div>

        <div style={styles.heroContent}>
          <div style={styles.badge}>
            <span style={styles.badgeDot}></span>
            HELP CENTER
          </div>

          <h1 style={styles.heroTitle}>
            Frequently Asked{" "}
            <span style={styles.heroGradient}>Questions</span>
          </h1>

          <p style={styles.heroText}>
            Find quick answers to common questions about courses, accounts,
            payments, certificates and technical support.
          </p>
        </div>
      </section>

      {/* ================= SEARCH ================= */}
      <div style={styles.searchWrapper}>
        <div style={styles.searchBox}>
          <span style={styles.searchIcon}>⌕</span>

          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setOpenIndex(null);
            }}
            placeholder="Search your question..."
            style={styles.searchInput}
          />

          {search && (
            <button
              type="button"
              style={styles.clearButton}
              onClick={() => setSearch("")}
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* ================= FAQ ================= */}
      <main style={styles.main}>
        {search ? (
          filteredFaqs.length > 0 ? (
            <div>
              <div
                style={{
                  marginBottom: "20px",
                  color: "#64748b",
                  fontSize: "13px",
                  fontWeight: 600,
                }}
              >
                {filteredFaqs.length} result
                {filteredFaqs.length !== 1 ? "s" : ""} found
              </div>

              {filteredFaqs.map((item, index) => {
                const isOpen = openIndex === index;

                return (
                  <div
                    key={index}
                    style={{
                      ...styles.faqItem,
                      ...(isOpen
                        ? {
                            borderColor: "#c7d2fe",
                            boxShadow:
                              "0 12px 30px rgba(79,70,229,.09)",
                          }
                        : {}),
                    }}
                  >
                    <div
                      style={{
                        ...styles.faqQuestion,
                        ...(isOpen ? styles.faqQuestionOpen : {}),
                      }}
                      onClick={() => toggleFAQ(index)}
                    >
                      <div style={styles.questionText}>
                        <span style={styles.questionNumber}>
                          {String(index + 1).padStart(2, "0")}
                        </span>

                        <span>{item.q}</span>
                      </div>

                      <span
                        style={{
                          ...styles.plus,
                          ...(isOpen ? styles.plusOpen : {}),
                        }}
                      >
                        +
                      </span>
                    </div>

                    {isOpen && (
                      <div style={styles.answer}>
                        <strong
                          style={{
                            color: "#4f46e5",
                            fontSize: "12px",
                          }}
                        >
                          {item.category}
                        </strong>

                        <div style={{ marginTop: "7px" }}>
                          {item.a}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={styles.noResult}>
              <div style={{ fontSize: "42px", marginBottom: "12px" }}>
                🔎
              </div>

              <h3
                style={{
                  margin: "0 0 8px",
                  fontSize: "20px",
                  color: "#111827",
                }}
              >
                No questions found
              </h3>

              <p
                style={{
                  margin: 0,
                  color: "#64748b",
                  fontSize: "14px",
                }}
              >
                Try searching with a different keyword.
              </p>
            </div>
          )
        ) : (
          faqData.map((category, catIndex) => (
            <section key={catIndex} style={styles.category}>
              <div style={styles.categoryHeader}>
                <div style={styles.categoryIcon}>{category.icon}</div>

                <h2 style={styles.categoryTitle}>
                  {category.category}
                </h2>

                <span style={styles.categoryCount}>
                  {category.items.length} QUESTIONS
                </span>
              </div>

              {category.items.map((item, itemIndex) => {
                const globalIndex =
                  faqData
                    .slice(0, catIndex)
                    .reduce(
                      (total, current) => total + current.items.length,
                      0
                    ) + itemIndex;

                const isOpen = openIndex === globalIndex;

                return (
                  <div
                    key={globalIndex}
                    style={{
                      ...styles.faqItem,
                      ...(isOpen
                        ? {
                            borderColor: "#c7d2fe",
                            boxShadow:
                              "0 14px 35px rgba(79,70,229,.10)",
                            transform: "translateY(-1px)",
                          }
                        : {}),
                    }}
                  >
                    <div
                      style={{
                        ...styles.faqQuestion,
                        ...(isOpen ? styles.faqQuestionOpen : {}),
                      }}
                      onClick={() => toggleFAQ(globalIndex)}
                    >
                      <div style={styles.questionText}>
                        <span
                          style={{
                            ...styles.questionNumber,
                            ...(isOpen
                              ? {
                                  background: "#e0e7ff",
                                  color: "#4f46e5",
                                }
                              : {}),
                          }}
                        >
                          {String(itemIndex + 1).padStart(2, "0")}
                        </span>

                        <span>{item.q}</span>
                      </div>

                      <span
                        style={{
                          ...styles.plus,
                          ...(isOpen ? styles.plusOpen : {}),
                        }}
                      >
                        +
                      </span>
                    </div>

                    {isOpen && (
                      <div style={styles.answer}>
                        {item.a}
                      </div>
                    )}
                  </div>
                );
              })}
            </section>
          ))
        )}

        {/* ================= CONTACT CTA ================= */}
        <div style={styles.contact}>
          <h2 style={styles.contactTitle}>
            Still have questions?
          </h2>

          <p style={styles.contactText}>
            Can't find what you're looking for? Our support team is ready to
            help you with any questions or issues.
          </p>

          <a
            href="/ContactUs"
            style={styles.contactButton}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow =
                "0 10px 25px rgba(0,0,0,.15)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            Contact Support →
          </a>
        </div>
      </main>

      {/* ================= FOOTER ================= */}
      <footer style={styles.footer}>
        © 2026 School Hub. All Rights Reserved.
      </footer>
    </div>
  );
};

export default FAQs;

